import { useCallback, useEffect, useState } from 'react';

const ARMED = Symbol('armed');

/**
 * One-shot strike-through animation for the Bug and CVE tables (STRY0038457).
 *
 * `arm()` is called when a version opens. Each table then animates its
 * crossed-out rows the first time it is on screen, and stops for good on the
 * first change to what it shows. Paging back to where it started does not
 * bring the animation back, and neither does saving a review state.
 *
 * @param views  `{ [table]: viewKey | null }`. The view key describes what the
 *               table is showing (page, search, filters, sort); null means the
 *               table is not on screen.
 * @returns `{ active: { [table]: boolean }, arm }`
 *
 * `active` is decided in render, by comparing the current view with the view
 * the animation started on. Clearing it from an effect alone would let a page
 * change paint one frame with the animation class still on the new rows.
 */
export default function useStrikeIntro(views) {
  const [intro, setIntro] = useState({});

  const active = {};
  Object.entries(views).forEach(([table, view]) => {
    const slot = intro[table];
    active[table] = view !== null && view !== undefined && (slot === ARMED || slot === view);
  });

  const viewsKey = JSON.stringify(views);
  useEffect(() => {
    setIntro(prev => {
      let changed = false;
      const next = { ...prev };
      Object.entries(views).forEach(([table, view]) => {
        const slot = prev[table];
        if (slot === undefined || slot === null) return;
        const onScreen = view !== null && view !== undefined;
        if (slot === ARMED) {
          // Not on screen yet (the CVE tab before it is opened): stay armed.
          if (onScreen) {
            next[table] = view;
            changed = true;
          }
          return;
        }
        if (!onScreen || slot !== view) {
          next[table] = null;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // `views` is read through viewsKey, its serialised form. `intro` is a
    // dependency so an arm() that leaves the views unchanged is still captured.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewsKey, intro]);

  const tables = Object.keys(views).join('|');
  const arm = useCallback(() => {
    setIntro(Object.fromEntries(tables.split('|').map(table => [table, ARMED])));
  }, [tables]);

  return { active, arm };
}
